import React, { useState, useEffect, forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import phoneMasks from './PhoneMasks/PhoneMasks';
import { Label } from '../Label/Label';
import { IMaskInput } from 'react-imask';
import { CountryDropdown } from '@/components/ui/country-dropdown';
import { countries } from 'country-data-list';

interface PhoneInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label: string;
  phone_prefix?: string;
  isPhonePrefixFixed?: boolean;
  helperText?: string;
  onChangePhone: (value: string, prefix: string) => void;
  value?: string;
  defaultValue?: string;
  disabled?: boolean;
}

export const PhoneInput = forwardRef<HTMLInputElement, PhoneInputProps>(
  (
    {
      label,
      phone_prefix,
      isPhonePrefixFixed,
      helperText,
      value,
      defaultValue,
      onChangePhone,
      disabled,
    },
    ref
  ) => {
    const { t } = useTranslation('common');

    const cleanCode = (code: string) => code?.replace('+', '');

    const getAlpha2ByCallingCode = (code: string) => {
      // "+1" pode ser CA/US e o pacote costuma retornar CA primeiro.
      // Para o default automático, priorizamos US.
      if (code === '1') return 'US';

      return (
        countries.all.find((country) =>
          country.countryCallingCodes.includes(`+${code}`)
        )?.alpha2 || 'BR'
      );
    };

    const [selectedCode, setSelectedCode] = useState(
      cleanCode(phone_prefix) || '55'
    );
    const [selectedCountryCode, setSelectedCountryCode] = useState(
      getAlpha2ByCallingCode(cleanCode(phone_prefix) || '55')
    );

    const [isFocused, setIsFocused] = useState(false);
    const [phoneNumber, setPhoneNumber] = useState(value || defaultValue || '');

    useEffect(() => {
      if (value !== undefined) {
        setPhoneNumber(value);
      }
    }, [value]);

    useEffect(() => {
      if (phone_prefix) {
        const code = cleanCode(phone_prefix);
        setSelectedCode(code);
        setSelectedCountryCode(getAlpha2ByCallingCode(code));
      }
    }, [phone_prefix]);

    return (
      <div className='w-full flex gap-2 flex-col'>
        {label && <Label>{label}</Label>}
        <div
          className={`phone-input m-0 p-0 w-full flex focus:outline-none items-center rounded-lg overflow-hidden disabled:bg-bgDisabled border bg-[#09090B] ${
            helperText ? 'border-borderError' : 'border-colorBorder'
          }`}
        >
          <CountryDropdown
            defaultValue={selectedCountryCode}
            onChange={(item) => {
              const code = item.countryCallingCodes[0].replace('+', '');
              setSelectedCode(code);
              setSelectedCountryCode(item.alpha2);
              onChangePhone(phoneNumber, code);
            }}
            disabled={disabled || isPhonePrefixFixed}
            slim
            phone
            data-cy='country-dropdown'
          />

          <IMaskInput
            ref={ref}
            mask={phoneMasks[`+${selectedCode}`] || '000000000000000'}
            value={phoneNumber}
            onAccept={(value) => {
              setPhoneNumber(value);
              onChangePhone(value, selectedCode);
            }}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            data-cy='phone-input'
            placeholder={t('phone_placeholder')}
            disabled={disabled}
            className={`flex-1 px-1 py-2 text-white border-none focus:outline-none focus:ring-0 ${
              isFocused && !phoneNumber ? 'placeholder-opacity-0' : ''
            } ${
              disabled
                ? ' bg-bgDisabled opacity-50 cursor-not-allowed'
                : 'bg-background'
            }`}
          />
        </div>
        {helperText && <p className='text-sm text-error mt-1'>{helperText}</p>}
      </div>
    );
  }
);
